import React, { useState } from 'react';
import cn from 'classnames';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';
import IconFullScreenOn from '@creditflowresearch/uilib/Icon/IconFullScreenOn';
import IconFullScreenOff from '@creditflowresearch/uilib/Icon/IconFullScreenOff';
import Button from '@creditflowresearch/uilib/Button';
import Flex from '@creditflowresearch/uilib/Flex';
import Grid from '@creditflowresearch/uilib/Grid';
import GridItem from '@creditflowresearch/uilib/GridItem';
import { useAiSidebarContext } from '@/creditflowresearch/application/AiSidebar/AiSidebarContext';
import {
  AskAiChatView,
  ChatHeaderActions,
  ChatHistoryPanel,
  ChatTitleMenu,
} from '@/creditflowresearch/ui/components/AskAiChat';
import ResizeHandle from './ResizeHandle';
import AiSidebarStyles from './AiSidebarStyles';

import styles from './aiSidebar.module.scss';

const AiSidebar = () => {
  const { isSplit, isOpen, isFullScreen, close, toggleFullScreen } = useAiSidebarContext();
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(false);
  const hasHistoryPanel = isSplit && isFullScreen;
  const isRail = hasHistoryPanel && isHistoryCollapsed;
  const isCovering = !isSplit || isFullScreen;
  const isForcedOverlay = !isSplit;

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLElement>): void => {
    if (event.target === event.currentTarget && event.propertyName === 'width') {
      window.dispatchEvent(new Event('resize'));
    }
  };

  if (!isSplit && !isOpen) {
    return null;
  }

  const actions = (
    <>
      <ChatHeaderActions />
      { isSplit && (
        <Button
          variant="text"
          size="S"
          icon={ isFullScreen ? <IconFullScreenOff /> : <IconFullScreenOn /> }
          iconSize={ 16 }
          onClick={ toggleFullScreen }
          ariaLabel={ isFullScreen ? 'Exit full screen' : 'Open full screen' }
          dataTest="aiSidebarFullScreenButton"
        />
      ) }
      <Button
        variant="text"
        size="S"
        icon={ <IconClose /> }
        iconSize={ 12 }
        onClick={ close }
        ariaLabel="Close Ask AI"
        dataTest="aiSidebarCloseButton"
      />
    </>
  );

  return (
    <aside
      className={ cn(styles.sidebar, {
        [styles.overlay]: isCovering,
        [styles.instant]: isForcedOverlay,
      }) }
      onTransitionEnd={ handleTransitionEnd }
      data-test="aiSidebar"
    >
      <AiSidebarStyles />
      { isOpen && (
        <Grid
          templateColumns={ hasHistoryPanel ? 'auto minmax(0, 1fr)' : undefined }
          templateRows="auto minmax(0, 1fr)"
          className={ cn(styles.inner, {
            [styles.instant]: isForcedOverlay,
            [styles.innerWithHistory]: hasHistoryPanel,
          }) }
        >
          { isSplit && !isFullScreen && <ResizeHandle /> }
          { hasHistoryPanel && (
            <GridItem rowSpan="full" className={ styles.history }>
              <ChatHistoryPanel
                isCollapsed={ isHistoryCollapsed }
                onToggle={ () => setIsHistoryCollapsed((value) => !value) }
                actions={ actions }
              />
            </GridItem>
          ) }
          <div className={ styles.header }>
            <ChatTitleMenu />
            { !isRail && (
              <Flex align="center" gap={ 20 }>
                { actions }
              </Flex>
            ) }
          </div>
          <div className={ styles.content }>
            <AskAiChatView />
          </div>
        </Grid>
      ) }
    </aside>
  );
};

export default AiSidebar;
